import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { buildTimelineLayout, generateDateTicks } from "../utils/timelineLayout";
import EventCard from "./EventCard";

const LANE_SPACING = 44;
const LABEL_MARGIN = 72;
const PADDING_HEAD = 40;
const PADDING_TAIL = 80;
const NODE_RADIUS = 12;
const NATURAL_PX_PER_DAY = 0.85;

export default function Timeline({ events, orientation, expanded, onEventClick }) {
  const wrapperRef = useRef(null);
  const [viewport, setViewport] = useState({ width: 800, height: 500 });
  const [hoverCard, setHoverCard] = useState(null); // { event, x, y }
  const [hoveredEventId, setHoveredEventId] = useState(null);

  const layout = useMemo(() => buildTimelineLayout(events), [events]);
  const { treeNodes, connectors, branchLines, minDate, maxDate, laneCount } = layout;
  const isHorizontal = orientation === "horizontal";

  const eventsById = useMemo(() => {
    const map = {};
    for (const e of events) map[e.id] = e;
    return map;
  }, [events]);

  // Immediate parent + children of the hovered event
  const relatedIds = useMemo(() => {
    if (!hoveredEventId) return null;
    const ids = new Set([hoveredEventId]);
    const ev = eventsById[hoveredEventId];
    if (ev?.parentId) ids.add(ev.parentId);
    for (const e of events) {
      if (e.parentId === hoveredEventId) ids.add(e.id);
    }
    return ids;
  }, [hoveredEventId, events, eventsById]);

  const isRelated = useCallback(
    (eventId) => !relatedIds || relatedIds.has(eventId),
    [relatedIds],
  );

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const measure = () => {
      setViewport({
        width: Math.max(el.clientWidth, 320),
        height: Math.max(el.clientHeight, 240),
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const totalDays = useMemo(() => {
    if (!minDate || !maxDate) return 365;
    const diff = new Date(maxDate).getTime() - new Date(minDate).getTime();
    return Math.max(diff / (1000 * 60 * 60 * 24), 30);
  }, [minDate, maxDate]);

  // Natural (expanded) size vs fit-to-screen
  const naturalTimeLength = Math.max(totalDays * NATURAL_PX_PER_DAY + PADDING_HEAD + PADDING_TAIL, 640);
  const naturalLaneLength = Math.max((Math.max(laneCount, 1) - 1) * LANE_SPACING + LABEL_MARGIN + 160, 220);

  let timeAxisLength;
  let laneAxisLength;
  let scale = 1;

  if (expanded) {
    timeAxisLength = naturalTimeLength;
    laneAxisLength = naturalLaneLength;
  } else {
    // Fit content into viewport: size axes to available space, then scale if needed
    if (isHorizontal) {
      timeAxisLength = Math.max(viewport.width - 8, 400);
      laneAxisLength = Math.min(naturalLaneLength, Math.max(viewport.height - 8, 180));
    } else {
      timeAxisLength = Math.max(viewport.height - 8, 400);
      laneAxisLength = Math.min(naturalLaneLength, Math.max(viewport.width - 8, 180));
    }
  }

  const svgWidth = isHorizontal ? timeAxisLength : laneAxisLength;
  const svgHeight = isHorizontal ? laneAxisLength : timeAxisLength;

  if (!expanded) {
    const fitScale = Math.min(viewport.width / svgWidth, viewport.height / svgHeight, 1);
    scale = Number.isFinite(fitScale) && fitScale > 0 ? fitScale : 1;
  }

  const timeRange = timeAxisLength - PADDING_HEAD - PADDING_TAIL;

  const dateTicks = useMemo(
    () => generateDateTicks(minDate, maxDate, Math.max(timeRange, 100)),
    [minDate, maxDate, timeRange],
  );

  function dateToTimeCoord(dateStr) {
    const date = new Date(dateStr);
    const min = new Date(minDate);
    const max = new Date(maxDate);
    const range = max.getTime() - min.getTime();
    if (range === 0) return PADDING_HEAD + timeRange / 2;
    return PADDING_HEAD + ((date.getTime() - min.getTime()) / range) * timeRange;
  }

  function laneToCoord(lane) {
    return LABEL_MARGIN + lane * LANE_SPACING;
  }

  const nodePositions = useMemo(() => {
    const positions = {};
    for (const node of treeNodes) {
      const timeCoord = dateToTimeCoord(node.date);
      const laneCoord = laneToCoord(node.lane);
      positions[node.id] = {
        x: isHorizontal ? timeCoord : laneCoord,
        y: isHorizontal ? laneCoord : timeCoord,
      };
    }
    return positions;
  }, [treeNodes, minDate, maxDate, isHorizontal, timeRange, laneCount]);

  const tickPositions = useMemo(() => {
    return dateTicks.map((tick) => ({
      ...tick,
      coord: dateToTimeCoord(tick.date),
    }));
  }, [dateTicks, minDate, maxDate, timeRange]);

  const showHoverCard = useCallback((eventId, clientX, clientY) => {
    const event = eventsById[eventId];
    if (!event || !wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    setHoveredEventId(eventId);
    setHoverCard({
      event,
      x: clientX - rect.left + 12,
      y: clientY - rect.top + 12,
    });
  }, [eventsById]);

  const hideHoverCard = useCallback(() => {
    setHoveredEventId(null);
    setHoverCard(null);
  }, []);

  const findEventOnLaneAt = useCallback(
    (lane, timeCoord) => {
      const bl = branchLines.find((b) => b.lane === lane);
      if (!bl) return null;
      let best = null;
      let bestDist = Infinity;
      for (const id of bl.eventIds) {
        const ev = eventsById[id];
        if (!ev) continue;
        const start = dateToTimeCoord(ev.startDate);
        const end = dateToTimeCoord(ev.type === "range" && ev.endDate ? ev.endDate : ev.startDate);
        if (timeCoord >= start - 8 && timeCoord <= end + 8) {
          const mid = (start + end) / 2;
          const dist = Math.abs(timeCoord - mid);
          if (dist < bestDist) {
            bestDist = dist;
            best = id;
          }
        }
      }
      return best || bl.eventIds[0] || null;
    },
    [branchLines, eventsById, minDate, maxDate, timeRange],
  );

  // Empty state must come after all hooks
  const isEmpty = !treeNodes.length;

  const contentStyle = expanded
    ? { width: svgWidth, height: svgHeight, minWidth: svgWidth, minHeight: svgHeight }
    : {
        width: svgWidth,
        height: svgHeight,
        transform: `scale(${scale})`,
        transformOrigin: "top left",
      };

  return (
    <div
      ref={wrapperRef}
      className={`timeline-tree-wrapper${expanded ? " timeline-expanded" : " timeline-fit"}${isEmpty ? " timeline-empty-wrap" : ""}`}
      onMouseLeave={hideHoverCard}
    >
      {isEmpty ? (
        <div className="timeline-empty">
          <p>
            No events yet. Click the <strong>+</strong> button below to add your first event.
          </p>
        </div>
      ) : (
        <>
          <div className="timeline-tree-content" style={contentStyle}>
            <svg
              className="timeline-tree-svg"
              width={svgWidth}
              height={svgHeight}
              viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            >
              {/* Background grid / ticks */}
              {tickPositions.map((tp) => {
                const isYear = tp.kind === "year";
                if (isHorizontal) {
                  return (
                    <g key={tp.date}>
                      <line
                        x1={tp.coord}
                        y1={LABEL_MARGIN - 16}
                        x2={tp.coord}
                        y2={svgHeight}
                        stroke={isYear ? "var(--color-border)" : "var(--color-border-light)"}
                        strokeWidth={isYear ? 1 : 0.5}
                        strokeDasharray={isYear ? "" : "4,4"}
                      />
                      <text
                        x={tp.coord}
                        y={LABEL_MARGIN - 22}
                        textAnchor="middle"
                        className={isYear ? "tick-label tick-year" : "tick-label"}
                      >
                        {isYear ? tp.date.slice(0, 4) : formatMonthShort(tp.date)}
                      </text>
                    </g>
                  );
                }
                return (
                  <g key={tp.date}>
                    <line
                      x1={LABEL_MARGIN - 10}
                      y1={tp.coord}
                      x2={svgWidth}
                      y2={tp.coord}
                      stroke={isYear ? "var(--color-border)" : "var(--color-border-light)"}
                      strokeWidth={isYear ? 1 : 0.5}
                      strokeDasharray={isYear ? "" : "4,4"}
                    />
                    <text
                      x={LABEL_MARGIN - 16}
                      y={tp.coord + 4}
                      textAnchor="end"
                      className={isYear ? "tick-label tick-year" : "tick-label"}
                    >
                      {isYear ? tp.date.slice(0, 4) : formatMonthShort(tp.date)}
                    </text>
                    <line
                      x1={LABEL_MARGIN - 10}
                      y1={tp.coord}
                      x2={LABEL_MARGIN - 2}
                      y2={tp.coord}
                      stroke="var(--color-text-muted)"
                      strokeWidth={1}
                    />
                  </g>
                );
              })}

              {/* Branch lines + wide hit targets */}
              {branchLines.map((bl) => {
                const timeStart = dateToTimeCoord(bl.startDate);
                const timeEnd = dateToTimeCoord(bl.endDate);
                const laneC = laneToCoord(bl.lane);
                const x1 = isHorizontal ? timeStart : laneC;
                const y1 = isHorizontal ? laneC : timeStart;
                const x2 = isHorizontal ? timeEnd : laneC;
                const y2 = isHorizontal ? laneC : timeEnd;
                const laneRelated = bl.eventIds.some((id) => isRelated(id));
                const lineOpacity = relatedIds ? (laneRelated ? 0.85 : 0.12) : 0.45;
                const lineWidth = relatedIds && laneRelated ? 4 : 3;

                return (
                  <g key={`branch-${bl.lane}`} className="timeline-line-group">
                    <line
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                      stroke={bl.color}
                      strokeWidth={lineWidth}
                      strokeLinecap="round"
                      opacity={lineOpacity}
                      pointerEvents="none"
                    />
                    <line
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                      stroke="transparent"
                      strokeWidth={16}
                      strokeLinecap="round"
                      className="timeline-hit"
                      onMouseMove={(e) => {
                        const svg = e.currentTarget.ownerSVGElement;
                        const rect = svg.getBoundingClientRect();
                        const local = isHorizontal
                          ? ((e.clientX - rect.left) / rect.width) * svgWidth
                          : ((e.clientY - rect.top) / rect.height) * svgHeight;
                        const eventId = findEventOnLaneAt(bl.lane, local);
                        if (eventId) showHoverCard(eventId, e.clientX, e.clientY);
                      }}
                      onMouseLeave={hideHoverCard}
                      onClick={(e) => {
                        const svg = e.currentTarget.ownerSVGElement;
                        const rect = svg.getBoundingClientRect();
                        const local = isHorizontal
                          ? ((e.clientX - rect.left) / rect.width) * svgWidth
                          : ((e.clientY - rect.top) / rect.height) * svgHeight;
                        const eventId = findEventOnLaneAt(bl.lane, local);
                        const node = treeNodes.find((n) => n.eventId === eventId && n.type !== "range-end");
                        if (node && onEventClick) onEventClick(node);
                      }}
                    />
                  </g>
                );
              })}

              {/* Range connectors + hit targets */}
              {connectors
                .filter((c) => c.type === "range")
                .map((c, i) => {
                  const fromNode = treeNodes[c.fromIdx];
                  const toNode = treeNodes[c.toIdx];
                  const fromPos = nodePositions[fromNode?.id];
                  const toPos = nodePositions[toNode?.id];
                  if (!fromPos || !toPos) return null;
                  const x1 = isHorizontal ? fromPos.x + NODE_RADIUS : fromPos.x;
                  const y1 = isHorizontal ? fromPos.y : fromPos.y + NODE_RADIUS;
                  const x2 = isHorizontal ? toPos.x - NODE_RADIUS : toPos.x;
                  const y2 = isHorizontal ? toPos.y : toPos.y - NODE_RADIUS;
                  const active = isRelated(c.eventId);
                  return (
                    <g key={`range-conn-${i}`} className="timeline-line-group">
                      <line
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke={c.color}
                        strokeWidth={active && relatedIds ? 4 : 3}
                        strokeLinecap="round"
                        opacity={relatedIds ? (active ? 1 : 0.12) : 0.75}
                        pointerEvents="none"
                      />
                      <line
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke="transparent"
                        strokeWidth={16}
                        strokeLinecap="round"
                        className="timeline-hit"
                        onMouseMove={(e) => showHoverCard(c.eventId, e.clientX, e.clientY)}
                        onMouseLeave={hideHoverCard}
                        onClick={() => {
                          const node = treeNodes.find((n) => n.eventId === c.eventId && n.type !== "range-end");
                          if (node && onEventClick) onEventClick(node);
                        }}
                      />
                    </g>
                  );
                })}

              {/* Bifurcation connectors — branch at child's start date from parent lane */}
              {connectors
                .filter((c) => c.type === "bifurcation")
                .map((c, i) => {
                  const childNode = treeNodes.find((n) => n.eventId === c.eventId && n.type !== "range-end");
                  const parentNode = treeNodes.find((n) => n.eventId === c.parentId && n.type !== "range-end");
                  if (!childNode || !parentNode) return null;
                  const childPos = nodePositions[childNode.id];
                  const parentPos = nodePositions[parentNode.id];
                  if (!childPos || !parentPos) return null;

                  // Fork from the parent lane at the child's start time (short elbow),
                  // instead of a long diagonal from the parent's start date.
                  const pathD = bifurcationPath(parentPos, childPos, isHorizontal, NODE_RADIUS);
                  const active = isRelated(c.eventId) || isRelated(c.parentId);

                  return (
                    <g key={`bif-conn-${i}`}>
                      <path
                        d={pathD}
                        stroke={c.color}
                        strokeWidth={active && relatedIds ? 2.5 : 2}
                        strokeDasharray="6,3"
                        fill="none"
                        opacity={relatedIds ? (active ? 1 : 0.08) : 0.6}
                        pointerEvents="none"
                      />
                      <path
                        d={pathD}
                        stroke="transparent"
                        strokeWidth={14}
                        fill="none"
                        className="timeline-hit"
                        onMouseMove={(e) => showHoverCard(c.eventId, e.clientX, e.clientY)}
                        onMouseLeave={hideHoverCard}
                        onClick={() => {
                          if (childNode && onEventClick) onEventClick(childNode);
                        }}
                      />
                    </g>
                  );
                })}
            </svg>

            <div className="timeline-nodes-overlay" style={{ width: svgWidth, height: svgHeight }}>
              {treeNodes.map((node) => {
                const pos = nodePositions[node.id];
                if (!pos) return null;
                const isRangeEnd = node.type === "range-end";
                const showLabel = node.type !== "range-end";
                const dimmed = relatedIds && !relatedIds.has(node.eventId);
                const highlighted = relatedIds && relatedIds.has(node.eventId);
                return (
                  <EventCard
                    key={node.id}
                    event={node}
                    x={pos.x}
                    y={pos.y}
                    isRangeEnd={isRangeEnd}
                    showLabel={showLabel}
                    dimmed={dimmed}
                    highlighted={highlighted}
                    onClick={onEventClick}
                    onHoverCard={(ev, clientX, clientY) => {
                      if (ev) showHoverCard(node.eventId, clientX, clientY);
                      else hideHoverCard();
                    }}
                  />
                );
              })}
            </div>
          </div>

          {hoverCard && (
            <div className="event-hover-card" style={{ left: hoverCard.x, top: hoverCard.y }}>
              <div className="event-hover-card-title" style={{ color: hoverCard.event.color }}>
                {hoverCard.event.title}
              </div>
              <div className="event-hover-card-dates">
                {hoverCard.event.startDate}
                {hoverCard.event.endDate ? ` → ${hoverCard.event.endDate}` : ""}
              </div>
              {hoverCard.event.description && <p className="event-hover-card-desc">{hoverCard.event.description}</p>}
              {hoverCard.event.image && (
                <div className="event-hover-card-img">
                  <img src={hoverCard.event.image} alt={hoverCard.event.title} />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function formatMonthShort(dateStr) {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const m = parseInt(dateStr.slice(5, 7), 10) - 1;
  return months[m] || "";
}

/**
 * Short elbow from the parent lane at the child's start time → child node.
 * Avoids long diagonals when the child is far from the parent's start date.
 */
function bifurcationPath(parentPos, childPos, isHorizontal, nodeRadius) {
  if (isHorizontal) {
    const forkX = childPos.x;
    const forkY = parentPos.y;
    const endX = childPos.x;
    const endY = childPos.y;
    const midY = (forkY + endY) / 2;
    // Stay on parent lane, then curve down into the child
    return `M ${forkX} ${forkY} C ${forkX} ${midY}, ${endX} ${midY}, ${endX} ${endY - Math.sign(endY - forkY || 1) * nodeRadius * 0.35}`;
  }

  const forkX = parentPos.x;
  const forkY = childPos.y;
  const endX = childPos.x;
  const endY = childPos.y;
  const midX = (forkX + endX) / 2;
  return `M ${forkX} ${forkY} C ${midX} ${forkY}, ${midX} ${endY}, ${endX - Math.sign(endX - forkX || 1) * nodeRadius * 0.35} ${endY}`;
}
